import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  canReactivateYowanSeminarRecord,
  fetchYowanAdminSeminarDetail,
  getYowanAdminPublicVisibilityStatusUi,
  getYowanPublicVisibilityDiagnostics,
  getYowanPublicVisibilityMessages,
  getYowanSeminarReactivateIssues,
  getYowanSeminarSlotWarningIssues,
  reactivateYowanAdminSeminar,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const secondaryActionButtonStyle = {
  background: 'transparent',
  border: '1px solid currentColor',
  color: 'inherit',
};

const ReadOnlyField = ({ label, value }) => (
  <label className="c-form__field yowan-admin-readonly-field">
    <span className="c-form__label">{label}</span>
    <input className="c-form__control" readOnly type="text" value={value || '-'} />
  </label>
);

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
    <header className="yowan-admin-event-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const PublicVisibilityPanel = ({ visibility, emptyMessage }) => {
  const statusUi = getYowanAdminPublicVisibilityStatusUi(visibility);
  const messages = getYowanPublicVisibilityMessages(visibility);
  const diagnostics = getYowanPublicVisibilityDiagnostics(visibility);

  return (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">公開面表示判定</p>
        <StatusBadge label={statusUi.label} tone={statusUi.tone} />
      </div>
      {diagnostics.length ? (
        <dl className="yowan-admin-event-detail__definition">
          {diagnostics.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {messages.length ? (
        <ul className="yowan-admin-public-visibility__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : (
        <p className="yowan-admin-public-visibility__message">
          {emptyMessage || '公開面に表示できる状態です。'}
        </p>
      )}
    </section>
  );
};

const AdminStateNoticePanel = ({ title, issues = [] }) => (
  issues.length ? (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">{title}</p>
      </div>
      <ul className="yowan-admin-public-visibility__list">
        {issues.map((issue) => (
          <li key={issue.code || issue.label}>{issue.message}</li>
        ))}
      </ul>
    </section>
  ) : null
);

const getReactivateErrorMessage = (error) => {
  const code = error?.response?.data?.code || '';

  if (code === 'seminar_already_active') {
    return 'このセミナーはすでに運用中です。一覧または詳細画面で状態を確認してください。';
  }

  return 'セミナーを運用再開できませんでした。状態とログイン状態を確認してください。';
};

const AdminEventReactivatePage = () => {
  const content = yowanPageContent['/admin/events/:eventId/reactivate'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReactivating, setIsReactivating] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');
    setActionMessage('');

    fetchYowanAdminSeminarDetail(eventId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('セミナー運用再開情報を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventId]);

  if (!content) {
    return null;
  }

  const listHref = appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch);
  const detailHref = record ? appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}`, preservedDomainSearch) : '';
  const editHref = record ? appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/edit?kind=seminar`, preservedDomainSearch) : '';
  const canReactivate = canReactivateYowanSeminarRecord(record);
  const slots = Array.isArray(record?.slots) ? record.slots : [];
  const publicVisibilityUi = getYowanAdminPublicVisibilityStatusUi(record?.publicVisibility);
  const reactivateIssues = getYowanSeminarReactivateIssues(record || {});
  const slotWarningIssues = getYowanSeminarSlotWarningIssues(record || {});
  const isPubliclyListed = record?.publicVisibility?.isPubliclyListed === true;
  const disabledReactivateLabel = isPubliclyListed ? '公開面復旧済み' : '復旧対象なし';

  const handleReactivateSeminar = async () => {
    if (!record?.id || isReactivating || !canReactivate) {
      return;
    }

    const confirmMessage = [
      content.confirmMessage,
      '',
      `対象セミナー: ${record.title || record.id}`,
      '',
      'この操作では、中止処理で落ちた運用停止状態・公開設定・開催コマの表示/受付状態を復旧します。',
      '単独セミナーの内部親シリーズ停止状態が残っている場合も同時に復旧します。',
      'セミナー全体の申込受付設定は現在の値を維持します。',
    ].join('\n');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsReactivating(true);
    setActionMessage('セミナー運用再開処理を実行しています。');

    try {
      const reactivatedRecord = await reactivateYowanAdminSeminar(record.id);
      let latestRecord = reactivatedRecord;

      try {
        latestRecord = await fetchYowanAdminSeminarDetail(record.id);
      } catch (refreshError) {
        latestRecord = reactivatedRecord;
      }

      setRecord(latestRecord);
      const latestVisibility = latestRecord?.publicVisibility || {};
      if (latestVisibility.isPubliclyListed === true) {
        setActionMessage('セミナー運用再開処理を実行しました。公開面へ表示できる状態です。');
      } else {
        setActionMessage('セミナー運用再開処理を実行しましたが、公開面表示判定に未解消の条件があります。下の表示判定を確認してください。');
      }
    } catch (error) {
      setActionMessage(getReactivateErrorMessage(error));
    } finally {
      setIsReactivating(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={record ? `${record.title} 運用再開` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="list" to={listHref}>セミナー一覧</Link>,
          detailHref ? <Link key="detail" to={detailHref}>詳細</Link> : null,
          editHref ? <Link key="edit" to={editHref}>編集</Link> : null,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="セミナー運用再開情報を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <DetailSection title="対象セミナー">
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '運用中' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
                <StatusBadge label={record.acceptsApplications ? '申込受付中' : '申込受付停止中'} tone={record.acceptsApplications ? 'published' : 'draft'} />
                <StatusBadge label={publicVisibilityUi.label} tone={publicVisibilityUi.tone} />
              </div>
              <PublicVisibilityPanel
                visibility={record.publicVisibility}
                emptyMessage="公開面へ表示できる状態です。申込受付の可否は「申込受付中」と開催コマごとの受付状態で別に制御します。"
              />
              <AdminStateNoticePanel title="運用再開が必要な理由" issues={reactivateIssues} />
              <AdminStateNoticePanel title="開催コマの注意" issues={slotWarningIssues} />
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="セミナー名" value={record.title} />
                <ReadOnlyField label="シリーズ" value={record.series?.title || '-'} />
                <ReadOnlyField label="参加者" value={`${record.applicationsCount || 0}名`} />
                <ReadOnlyField label="開催コマ" value={`${slots.length}件`} />
                <ReadOnlyField label="公開パス" value={record.publicPath || record.series?.publicPath || record.href} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
              </div>
            </DetailSection>

            <DetailSection
              title="運用再開で変更されること"
              lead="このページでは、通常編集で保存するタイトル・本文・画像は変更しません。中止処理で落ちた運用停止状態、公開設定、開催コマの表示/受付状態を復旧します。"
            >
              <ul className="yowan-admin-detail-list">
                <li>セミナー本体が停止中の場合は、運用停止状態を解除します。</li>
                <li>中止処理で「公開する」が無効になっている場合は、公開設定を復旧します。</li>
                <li>中止処理で開催コマが非表示・受付停止になっている場合は、各コマを表示・受付中へ復旧します。</li>
                <li>単独セミナーの内部親シリーズが停止中の場合は、公開面ゲートを復旧します。</li>
                <li>「申込受付中」の設定は現在の値を維持します。申込受付を止めていた場合は、公開面に戻っても申込不可状態を維持します。</li>
                <li>通知メールは送信しません。</li>
              </ul>
            </DetailSection>

            <DetailSection title="開催コマの現在値">
              <div className="yowan-admin-resource-table-wrap">
                <table className="yowan-admin-resource-table">
                  <thead>
                    <tr>
                      <th scope="col">開催コマ</th>
                      <th scope="col">日時</th>
                      <th scope="col">表示</th>
                      <th scope="col">受付</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.length ? slots.map((slot, index) => (
                      <tr key={slot.id || index}>
                        <td>{slot.title || `開催コマ ${index + 1}`}</td>
                        <td>{slot.datetimeLabel || '-'}</td>
                        <td>
                          <StatusBadge label={slot.isActive ? '表示' : '非表示'} tone={slot.isActive ? 'published' : 'draft'} />
                        </td>
                        <td>
                          <StatusBadge label={slot.isOpen ? '受付中' : '受付停止中'} tone={slot.isOpen ? 'published' : 'draft'} />
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={4}>開催コマは登録されていません。</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </DetailSection>

            <DetailSection title="運用再開">
              <p className="yowan-admin-detail-panel__lead">
                中止処理で落ちた公開面表示に必要な状態を復旧します。処理後はこのページの公開面表示判定で、公開面へ戻れる状態か確認してください。
              </p>
              <div className="yowan-admin-form__actions">
                <button
                  type="button"
                  className="c-form__submit"
                  disabled={isReactivating || !canReactivate}
                  onClick={handleReactivateSeminar}
                >
                  {!canReactivate ? disabledReactivateLabel : isReactivating ? '運用再開処理中' : '運用再開する'}
                </button>
                <button
                  type="button"
                  className="c-form__submit"
                  style={secondaryActionButtonStyle}
                  onClick={() => navigate(detailHref || listHref)}
                >
                  詳細に戻る
                </button>
              </div>
              {!canReactivate ? (
                <p className="yowan-admin-form__message">
                  {isPubliclyListed
                    ? 'このセミナーは公開面へ表示できる状態です。追加の運用再開処理は不要です。'
                    : 'このセミナーは現在、運用再開または公開面復旧の対象として返されていません。状態を確認してください。'}
                </p>
              ) : null}
              {actionMessage ? <p className="yowan-admin-form__message">{actionMessage}</p> : null}
            </DetailSection>
          </div>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminEventReactivatePage;
